import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch, apiOpenBlob } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { showToast } from '../../../components/Toast';
import { oggiRoma, aggiungiGiorniIso, formatDataOraRoma, formatDataRoma } from '../../../utils/dateRoma';
import { raggruppaSlotPerGiorno } from '../../../pubblico/slotGiorni';
import { API_DOCS } from './SchedaAcquirentePercorsoPage.api';
import {
  BuonoBreve, SlotInterno, StatoAppuntamento, OrigineAppuntamento, UsoImmobile, Orientamento,
  BADGE_STATO_APPUNTAMENTO, ETICHETTA_STATO_APPUNTAMENTO, ETICHETTA_ORIGINE,
  ETICHETTA_TIPO_BUONO, ETICHETTA_STATO_BUONO, BADGE_STATO_BUONO,
} from './percorsoTipi';

interface Intervista {
  usoImmobile: UsoImmobile | null; orientamento: Orientamento | null;
  haProgettista: boolean | null; cucinaGiaScelta: boolean | null; stile: string | null; note: string | null;
}
interface AppuntamentoScheda {
  id: number; inizio: string; fine: string; numeroIncontro: number; stato: StatoAppuntamento; origine: OrigineAppuntamento;
  googleEventId: string | null; emailConfermaAt: string | null; erroreEmail: string | null; venditore: { id: number; nome: string };
}
interface DatiScheda {
  acquirente: {
    id: number; nome: string; cognome: string; unitaCodice: string; telefono: string | null; email: string | null;
    origine: 'MANUALE' | 'PAGINA_PUBBLICA'; venditoreRiferimento: { id: number; nome: string } | null;
  };
  commessa: { id: number; numero: string; descrizione: string; societaId: number; sede: { id: number; nome: string } };
  offerta: { id: number; codice: string; oggetto: string } | null;
  intervista: Partial<Intervista> | null;
  appuntamenti: AppuntamentoScheda[];
  buoni: BuonoBreve[];
  linkProssimoIncontro: string | null;
}

const MOD = 'commerciale.percorso_agenda';
const MOD_COMMESSE = 'commerciale.commesse';
const INTERVISTA_VUOTA: Intervista = { usoImmobile: null, orientamento: null, haProgettista: null, cucinaGiaScelta: null, stile: null, note: null };

// L'API restituisce l'intero record: del modulo si tengono (e si rispediscono) solo i campi dell'intervista.
function soloCampiIntervista(i: Partial<Intervista> | null): Intervista {
  return {
    usoImmobile: i?.usoImmobile ?? null, orientamento: i?.orientamento ?? null,
    haProgettista: i?.haProgettista ?? null, cucinaGiaScelta: i?.cucinaGiaScelta ?? null,
    stile: i?.stile ?? null, note: i?.note ?? null,
  };
}
function valoreBool(v: boolean | null): string {
  return v === null ? '' : v ? 'si' : 'no';
}
function leggiBool(v: string): boolean | null {
  return v === '' ? null : v === 'si';
}

export default function SchedaAcquirentePercorsoPage() {
  const { acquirenteId } = useParams<{ acquirenteId: string }>();
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canEdit = can(MOD, 'edit');
  // La scheda si apre anche dalla commessa, con il solo permesso sulle commesse: allora mancano le azioni dell'agenda.
  const canAgenda = can(MOD, 'view');
  const canCommesse = can(MOD_COMMESSE, 'view');
  const base = `/api/percorso-acquirenti/acquirenti/${acquirenteId}`;
  const [dati, setDati] = useState<DatiScheda | null>(null);
  const [intervista, setIntervista] = useState<Intervista>(INTERVISTA_VUOTA);
  // Intervista come salvata sul server (JSON): serve a sapere se il modulo ha modifiche non salvate.
  const [intervistaSalvata, setIntervistaSalvata] = useState('');
  const [errore, setErrore] = useState('');
  const [lavoro, setLavoro] = useState(false);
  const [slot, setSlot] = useState<SlotInterno[] | null>(null);
  const [caricandoSlot, setCaricandoSlot] = useState(false);
  const [daSlot, setDaSlot] = useState(oggiRoma());
  const richiestaCorrente = useRef(0);
  // Il modulo dell'intervista si riempie con la prima risposta valida; i ricaricamenti successivi non lo toccano.
  const intervistaDaCaricare = useRef(true);
  // Dopo il primo caricamento un ricaricamento fallito non deve sostituire la scheda con un errore.
  const haDati = useRef(false);

  const isDirty = intervistaSalvata !== '' && JSON.stringify(intervista) !== intervistaSalvata;
  useDirtyForm(isDirty, 'Intervista acquirente');

  useBloccoRecordContesto({
    recordSocietaId: dati?.commessa.societaId,
    label: dati ? `Acquirente ${dati.acquirente.cognome} ${dati.acquirente.nome} — unità ${dati.acquirente.unitaCodice}` : 'Scheda acquirente',
  });

  const carica = useCallback(() => {
    const richiesta = ++richiestaCorrente.current;
    // @api GET /api/percorso-acquirenti/acquirenti/:id -> {"success":true,"data":{"acquirente":"object","commessa":"object","offerta":"object","intervista":"object","appuntamenti":"array","buoni":"array","linkProssimoIncontro":"string"}}
    apiFetch<{ success: boolean; data: DatiScheda }>(base)
      .then(r => {
        if (richiesta !== richiestaCorrente.current) return;
        setDati(r.data);
        haDati.current = true;
        if (intervistaDaCaricare.current) {
          intervistaDaCaricare.current = false;
          const salvata = soloCampiIntervista(r.data.intervista);
          setIntervista(salvata);
          setIntervistaSalvata(JSON.stringify(salvata));
        }
      })
      .catch(e => {
        if (richiesta !== richiestaCorrente.current) return;
        if (haDati.current) showToast(e.message, 'error');
        else setErrore(e.message);
      });
  }, [base]);

  useEffect(() => {
    // Primo caricamento (o altra scheda): si riparte da zero.
    haDati.current = false;
    intervistaDaCaricare.current = true;
    setDati(null);
    setErrore('');
    carica();
  }, [carica]);

  // Restituisce true se l'azione è riuscita.
  const esegui = async (azione: () => Promise<void>): Promise<boolean> => {
    setLavoro(true);
    let riuscita = false;
    try {
      await azione();
      riuscita = true;
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setLavoro(false);
    }
    // Si ricarica anche dopo un errore: spesso sul server qualcosa è cambiato (es. "ricarica la pagina").
    carica();
    return riuscita;
  };

  const salvaIntervista = () => esegui(async () => {
    // Quanto spedito diventa il nuovo "salvato"; il ricaricamento successivo non tocca il modulo,
    // così ciò che si digita mentre la richiesta è in corso non va perso.
    const spedita = intervista;
    // @api PUT /api/percorso-acquirenti/acquirenti/:id/intervista {"usoImmobile":"string","orientamento":"string","haProgettista":"boolean","cucinaGiaScelta":"boolean","stile":"string","note":"string"} -> {"success":true}
    await apiFetch(`${base}/intervista`, { method: 'PUT', body: JSON.stringify(spedita) });
    setIntervistaSalvata(JSON.stringify(spedita));
    showToast('Intervista salvata');
  });

  const consegnaBuoni = () => esegui(async () => {
    // @api POST /api/percorso-acquirenti/acquirenti/:id/consegna-buoni -> {"success":true,"data":{"buoni":"array","emailInviata":"boolean","erroreEmail":"string"}}
    const r = await apiFetch<{ success: boolean; data: { emailInviata: boolean; erroreEmail: string | null } }>(`${base}/consegna-buoni`, { method: 'POST' });
    showToast(r.data.emailInviata ? 'Buoni consegnati e inviati per email' : `Buoni consegnati${r.data.erroreEmail ? ` — email non inviata: ${r.data.erroreEmail}` : ' (acquirente senza email)'}`,
      r.data.emailInviata ? 'success' : 'warning');
    await apiOpenBlob(`${base}/buoni/pdf`);
  });

  const cambiaStato = async (id: number, stato: StatoAppuntamento) => {
    if (stato === 'ANNULLATO' && !window.confirm("Annullare l'appuntamento? L'acquirente riceverà un'email.")) return;
    await esegui(async () => {
      // @api PUT /api/percorso-acquirenti/appuntamenti/:id/stato {"stato":"string"} -> {"success":true}
      await apiFetch(`/api/percorso-acquirenti/appuntamenti/${id}/stato`, { method: 'PUT', body: JSON.stringify({ stato }) });
    });
  };

  const reinviaEmail = (id: number) => esegui(async () => {
    // @api POST /api/percorso-acquirenti/appuntamenti/:id/reinvia-email -> {"success":true,"data":{"emailInviata":"boolean"}}
    const r = await apiFetch<{ success: boolean; data: { emailInviata: boolean } }>(`/api/percorso-acquirenti/appuntamenti/${id}/reinvia-email`, { method: 'POST' });
    showToast(r.data.emailInviata ? 'Email reinviata' : 'Email non inviata: controlla lo SMTP della sede', r.data.emailInviata ? 'success' : 'error');
  });

  const caricaSlot = (da: string) => {
    setDaSlot(da);
    setSlot(null);
    setCaricandoSlot(true);
    // @api GET /api/percorso-acquirenti/acquirenti/:id/slot?da -> {"success":true,"data":"array"}
    apiFetch<{ success: boolean; data: SlotInterno[] }>(`${base}/slot?da=${da}`)
      .then(r => setSlot(r.data))
      .catch(e => showToast(e.message, 'error'))
      .finally(() => setCaricandoSlot(false));
  };

  const prenota = async (inizio: string) => {
    const riuscita = await esegui(async () => {
      // @api POST /api/percorso-acquirenti/acquirenti/:id/appuntamenti {"inizio":"string"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch(`${base}/appuntamenti`, { method: 'POST', body: JSON.stringify({ inizio }) });
      setSlot(null);
      showToast('Appuntamento prenotato');
    });
    // Orario appena occupato (409) o altro errore: si rilegge l'elenco per non offrire più quello slot.
    if (!riuscita) caricaSlot(daSlot);
  };

  const copiaLink = async () => {
    if (!dati?.linkProssimoIncontro) return;
    try {
      await navigator.clipboard.writeText(dati.linkProssimoIncontro);
      showToast('Link copiato');
    } catch {
      // Clipboard non disponibile (contesto non sicuro o permesso negato).
      showToast('Impossibile copiare il link', 'error');
    }
  };

  const tornaA = canAgenda
    ? { to: '/commerciale/percorso/agenda', label: "Torna all'agenda" }
    : { to: dati ? `/commerciale/commesse/${dati.commessa.id}` : '/commerciale/commesse', label: dati ? 'Torna alla commessa' : 'Torna alle commesse' };

  if (errore) return <Layout><BopBackLink to={tornaA.to} label={tornaA.label} /><div className="bop-alert bop-alert--danger">{errore}</div></Layout>;
  if (!dati) return <Layout><BopBackLink to={tornaA.to} label={tornaA.label} /><div className="bop-empty-state">Caricamento…</div></Layout>;

  const { acquirente, commessa } = dati;
  const buoniAttivi = dati.buoni.filter(b => b.stato !== 'ANNULLATO');
  const adesso = Date.now();

  return (
    <Layout>
      <BopBackLink to={tornaA.to} label={tornaA.label} />
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">{acquirente.cognome} {acquirente.nome} — unità {acquirente.unitaCodice}</h1>
          <p className="bop-page-subtitle">
            {dati.offerta?.oggetto || commessa.descrizione} · commessa {commessa.numero} · {commessa.sede.nome}
            {acquirente.venditoreRiferimento ? ` · venditore di riferimento: ${acquirente.venditoreRiferimento.nome}` : ''}
          </p>
        </div>
        {canCommesse && (
          <div className="bop-flex">
            <button className="bop-btn bop-btn-secondary" onClick={() => navigate(`/commerciale/commesse/${commessa.id}`)}>Apri commessa</button>
          </div>
        )}
      </div>

      <div className="bop-percorso__griglia">
        <div className="bop-card">
          <div className="bop-card__header"><h2 className="bop-card__title">Intervista</h2></div>
          <div className="bop-card__body bop-percorso__sezione">
            <div className="bop-form-group">
              <label className="bop-label">La casa sarà</label>
              <select className="bop-select" value={intervista.usoImmobile ?? ''} disabled={!canEdit}
                onChange={e => setIntervista(i => ({ ...i, usoImmobile: (e.target.value || null) as UsoImmobile | null }))}>
                <option value="">—</option>
                <option value="ABITAZIONE">La loro abitazione</option>
                <option value="INVESTIMENTO">Un investimento</option>
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Orientamento dopo la brochure</label>
              <select className="bop-select" value={intervista.orientamento ?? ''} disabled={!canEdit}
                onChange={e => setIntervista(i => ({ ...i, orientamento: (e.target.value || null) as Orientamento | null }))}>
                <option value="">—</option>
                <option value="CAPITOLATO">Capitolato</option>
                <option value="EXTRA_CAPITOLATO">Extra-capitolato</option>
                <option value="DA_CAPIRE">Da capire</option>
              </select>
            </div>
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Ha un progettista</label>
                <select className="bop-select" value={valoreBool(intervista.haProgettista)} disabled={!canEdit}
                  onChange={e => setIntervista(i => ({ ...i, haProgettista: leggiBool(e.target.value) }))}>
                  <option value="">—</option><option value="si">Sì</option><option value="no">No</option>
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Cucina già scelta</label>
                <select className="bop-select" value={valoreBool(intervista.cucinaGiaScelta)} disabled={!canEdit}
                  onChange={e => setIntervista(i => ({ ...i, cucinaGiaScelta: leggiBool(e.target.value) }))}>
                  <option value="">—</option><option value="si">Sì</option><option value="no">No</option>
                </select>
              </div>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Gusto e stile</label>
              <input className="bop-input" value={intervista.stile ?? ''} disabled={!canEdit} onChange={e => setIntervista(i => ({ ...i, stile: e.target.value || null }))} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Note</label>
              <textarea className="bop-input bop-percorso__textarea" value={intervista.note ?? ''} disabled={!canEdit} onChange={e => setIntervista(i => ({ ...i, note: e.target.value || null }))} />
            </div>
            {canEdit && <div className="bop-percorso__riga"><button className="bop-btn bop-btn-primary" onClick={salvaIntervista} disabled={lavoro}>Salva intervista</button></div>}
          </div>
        </div>

        <div className="bop-card">
          <div className="bop-card__header"><h2 className="bop-card__title">Buoni Atrio e Aran</h2></div>
          <div className="bop-card__body bop-percorso__sezione">
            {buoniAttivi.length === 0 ? (
              <p className="bop-text-muted">Non ancora consegnati. Si consegnano a fine incontro 1, dopo l'intervista.</p>
            ) : (
              <table className="bop-table">
                <thead><tr><th>Buono</th><th>Codice</th><th>Scadenza</th><th>Stato</th></tr></thead>
                <tbody>
                  {buoniAttivi.map(b => (
                    <tr key={b.id}>
                      <td>{ETICHETTA_TIPO_BUONO[b.tipo]}</td>
                      <td><strong>{b.codice}</strong></td>
                      <td>{formatDataRoma(b.scadenzaAt)}</td>
                      <td><span className={`bop-badge ${BADGE_STATO_BUONO[b.stato]}`}>{ETICHETTA_STATO_BUONO[b.stato]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="bop-percorso__riga">
              {canEdit && buoniAttivi.length < 2 && <button className="bop-btn bop-btn-primary" onClick={consegnaBuoni} disabled={lavoro}>Consegna buoni</button>}
              {canAgenda && buoniAttivi.length > 0 && <button className="bop-btn bop-btn-secondary" onClick={() => apiOpenBlob(`${base}/buoni/pdf`).catch(e => showToast(e.message, 'error'))}>Stampa buoni</button>}
            </div>
            {dati.linkProssimoIncontro && (
              <div className="bop-percorso__riga">
                <span className="bop-label">Link per il prossimo incontro:</span>
                <span className="bop-percorso__link">{dati.linkProssimoIncontro}</span>
                <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={copiaLink}>Copia</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header"><h2 className="bop-card__title">Appuntamenti</h2></div>
        <div className="bop-card__body bop-percorso__sezione">
          <table className="bop-table">
            <thead><tr><th>Quando</th><th>Incontro</th><th>Venditore</th><th>Origine</th><th>Stato</th><th>Email</th><th></th></tr></thead>
            <tbody>
              {dati.appuntamenti.length === 0 ? (
                <tr><td colSpan={7}><div className="bop-empty-state">Nessun appuntamento</div></td></tr>
              ) : dati.appuntamenti.map(app => (
                <tr key={app.id}>
                  <td>{formatDataOraRoma(app.inizio)}</td>
                  <td>{app.numeroIncontro}</td>
                  <td>{app.venditore.nome}</td>
                  <td>{ETICHETTA_ORIGINE[app.origine]}</td>
                  <td><span className={`bop-badge ${BADGE_STATO_APPUNTAMENTO[app.stato]}`}>{ETICHETTA_STATO_APPUNTAMENTO[app.stato]}</span></td>
                  <td>{app.erroreEmail ? <span className="bop-text-danger">{app.erroreEmail}</span> : app.emailConfermaAt ? 'Inviata' : '—'}</td>
                  <td>
                    {canEdit && app.stato === 'PRENOTATO' && (
                      <div className="bop-percorso__riga">
                        <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => cambiaStato(app.id, 'SVOLTO')} disabled={lavoro}>Svolto</button>
                        <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => cambiaStato(app.id, 'NON_PRESENTATO')} disabled={lavoro}>Non presentato</button>
                        {/* Il reinvio ha senso solo per un appuntamento futuro (il server risponde 409 altrimenti) e con un indirizzo a cui scrivere. */}
                        {acquirente.email && new Date(app.inizio).getTime() > adesso && (
                          <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => reinviaEmail(app.id)} disabled={lavoro}>Reinvia email</button>
                        )}
                        <button className="bop-btn bop-btn-danger bop-btn-sm" onClick={() => cambiaStato(app.id, 'ANNULLATO')} disabled={lavoro}>Annulla</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {canEdit && !slot && !caricandoSlot && <div className="bop-percorso__riga"><button className="bop-btn bop-btn-primary" onClick={() => caricaSlot(oggiRoma())}>Prenota prossimo incontro</button></div>}
          {caricandoSlot && <p className="bop-text-muted">Caricamento orari…</p>}
          {slot && (
            <div className="bop-percorso__sezione">
              {slot.length === 0 && <p className="bop-text-muted">Nessun orario libero in queste due settimane.</p>}
              {raggruppaSlotPerGiorno(slot).map(g => (
                <div className="bop-percorso__sezione" key={g.giorno}>
                  <span className="bop-label">{g.etichetta}</span>
                  <div className="bop-percorso__slot">
                    {g.slot.map(s => (
                      <button key={s.inizio} className="bop-percorso__slot-btn" onClick={() => prenota(s.inizio)} disabled={lavoro}>
                        {s.ora} · {s.venditore.nome}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="bop-percorso__riga">
                <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => caricaSlot(aggiungiGiorniIso(daSlot, 14))}>Date successive</button>
                <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setSlot(null)}>Chiudi</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
